import type { ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { PartnerGateScreens } from './components/PartnerGateScreens';
import { PartnerShell } from './components/PartnerShell';
import { AddClientPage } from './pages/AddClientPage';
import { FirmStatusPage } from './pages/FirmStatusPage';
import { PartnerClientPage } from './pages/PartnerClientPage';
import { PartnerClientsPage } from './pages/PartnerClientsPage';
import { PartnerDashboardPage } from './pages/PartnerDashboardPage';
import { PartnerAgreementsPage, PartnerProfilePage, PartnerSecurityPage } from './pages/PartnerSettingsPages';
import { PartnerTeamPage } from './pages/PartnerTeamPage';
import { ClientWorkspacePage } from './pages/ClientWorkspacePage';
import { ClientSessionProvider } from './state/ClientSessionProvider';
import { PartnerProvider } from './state/PartnerProvider';
import { usePartner } from './state/usePartner';

/** Operating pages exist only for a firm that is Active. Any other status sees its status page instead (the server refuses the same requests regardless). */
function WhenActive({ children }: { children: ReactNode }) {
  const { membership } = usePartner();
  return membership.status === 'Active' ? <>{children}</> : <FirmStatusPage />;
}

/**
 * The whole Partner Portal, mounted by the app at /partner/*. Before any page it settles who the person is: no partner membership shows "You do not currently have access to this portal."
 * and a session without authenticator-app MFA shows the mandatory MFA step. Everything after that is chosen for the person's firm and role; the server authorizes every request again.
 */
export function PartnerPortal() {
  return (
    <PartnerProvider notReady={(load) => <PartnerGateScreens load={load} />}>
      <ClientSessionProvider>
      <PartnerShell>
        <Routes>
          <Route path="/partner/dashboard" element={<WhenActive><PartnerDashboardPage /></WhenActive>} />
          <Route path="/partner/clients" element={<WhenActive><PartnerClientsPage /></WhenActive>} />
          <Route path="/partner/clients/new" element={<WhenActive><AddClientPage /></WhenActive>} />
          <Route path="/partner/clients/:relationshipId/workspace" element={<WhenActive><ClientWorkspacePage /></WhenActive>} />
          <Route path="/partner/clients/:relationshipId" element={<WhenActive><PartnerClientPage /></WhenActive>} />
          <Route path="/partner/team" element={<PartnerTeamPage />} />
          <Route path="/partner/settings/profile" element={<PartnerProfilePage />} />
          <Route path="/partner/settings/security" element={<PartnerSecurityPage />} />
          <Route path="/partner/settings/agreements" element={<PartnerAgreementsPage />} />
          <Route path="*" element={<Navigate to="/partner/dashboard" replace />} />
        </Routes>
      </PartnerShell>
      </ClientSessionProvider>
    </PartnerProvider>
  );
}
